/**
 * Explore defaults derived from user Profile (target roles / location),
 * not hardcoded IC/Stripe demo strings.
 */

export type ExploreProfileHints = {
  targetRoles?: string[];
  location?: string;
  remote?: string;
};

/** Neutral fallbacks when Profile has no target roles yet. */
export const DEFAULT_EXPLORE_QUICK_STARTS = [
  "CTO or VP of Technology",
  "Head of Technology FinTech",
  "Digital transformation executive",
] as const;

function primaryLocation(location?: string): string | undefined {
  if (!location?.trim()) return undefined;
  return location.split(/[,(/]/)[0]?.trim() || undefined;
}

function remoteSuffix(remote?: string): string {
  const r = (remote ?? "").toLowerCase();
  if (r === "remote") return ", remote";
  if (r === "hybrid" || r.includes("hybrid")) {
    return ", hybrid or on-site preferred";
  }
  return "";
}

/** Up to 3 plain-language Search chips from Profile.targetRoles. */
export function buildExploreQuickStarts(
  profile?: ExploreProfileHints | null,
): string[] {
  const roles = (profile?.targetRoles ?? [])
    .map((r) => r.trim())
    .filter(Boolean);
  if (roles.length === 0) {
    return [...DEFAULT_EXPLORE_QUICK_STARTS];
  }

  const loc = primaryLocation(profile?.location);
  const remote = remoteSuffix(profile?.remote);
  const starts: string[] = [];

  for (const role of roles.slice(0, 3)) {
    let q = role;
    if (loc) q += ` in ${loc}`;
    q += remote;
    starts.push(q);
  }
  return starts;
}

/** Title keywords for Companies/Boards scan chips from target roles. */
export function targetRolesAsTitleKeywords(
  roles?: string[] | null,
): string[] {
  return (roles ?? [])
    .map((r) => r.trim())
    .filter(Boolean)
    .slice(0, 8);
}

export function buildExploreSearchPath(profile?: ExploreProfileHints | null): string {
  const [first] = buildExploreQuickStarts(profile);
  return `/explore?mode=ai&q=${encodeURIComponent(first)}`;
}
